import { useState } from "react";
import { toast } from "sonner";
import { api, fmtErr } from "../lib/api";

export const inp = "w-full rounded-none border border-neutral-300 bg-white px-3 py-2 text-sm outline-none transition-colors focus:border-[#D60024]";

export function Field({ label, children }) {
    return (
        <div>
            <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-neutral-500">{label}</span>
            {children}
        </div>
    );
}

export function T({ label, value, onChange, testId, type = "text" }) {
    return (
        <Field label={label}>
            <input type={type} data-testid={testId} className={inp} value={value ?? ""} onChange={(e) => onChange(type === "number" ? Number(e.target.value) : e.target.value)} />
        </Field>
    );
}

export function TA({ label, value, onChange, testId, rows = 3 }) {
    return (
        <Field label={label}>
            <textarea rows={rows} data-testid={testId} className={inp} value={value ?? ""} onChange={(e) => onChange(e.target.value)} />
        </Field>
    );
}

export function BI({ label, value, onChange, testId, rows }) {
    const v = value || {};
    const setL = (l) => (e) => onChange({ ...v, [l]: e.target.value });
    return (
        <Field label={label}>
            <div className="grid gap-2 sm:grid-cols-2">
                {["en", "pt"].map((l) =>
                    rows ? (
                        <textarea key={l} rows={rows} data-testid={testId ? `${testId}-${l}` : undefined} className={inp} placeholder={l.toUpperCase()} value={v[l] ?? ""} onChange={setL(l)} />
                    ) : (
                        <input key={l} data-testid={testId ? `${testId}-${l}` : undefined} className={inp} placeholder={l.toUpperCase()} value={v[l] ?? ""} onChange={setL(l)} />
                    )
                )}
            </div>
        </Field>
    );
}

export function SaveBar({ onSave, saving, testId }) {
    return (
        <div className="pt-2">
            <button
                onClick={onSave}
                disabled={saving}
                data-testid={testId}
                className="bg-[#0A0A0A] px-8 py-3 text-xs font-semibold uppercase tracking-[0.25em] text-white transition-colors hover:bg-[#D60024] disabled:opacity-50"
            >
                {saving ? "Saving…" : "Save changes"}
            </button>
        </div>
    );
}

export function Section({ title, children, defaultOpen = false }) {
    return (
        <details className="border border-neutral-200 bg-white" open={defaultOpen}>
            <summary className="cursor-pointer select-none px-5 py-4 text-[11px] font-semibold uppercase tracking-[0.25em] text-[#0A0A0A] hover:text-[#D60024]">
                {title}
            </summary>
            <div className="space-y-4 border-t border-neutral-200 p-5">{children}</div>
        </details>
    );
}

export function Img({ label, value, onChange, testId }) {
    const [uploading, setUploading] = useState(false);

    const pick = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", f);
            const { data } = await api.post("/admin/upload", fd);
            onChange(data.url);
            toast.success("Image uploaded");
        } catch (err) {
            toast.error(fmtErr(err.response?.data?.detail));
        } finally {
            setUploading(false);
            e.target.value = "";
        }
    };

    return (
        <Field label={label}>
            <div className="flex gap-2">
                <input className={inp} data-testid={testId} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder="https://… or upload from your computer" />
                <label
                    data-testid={testId ? `${testId}-upload` : undefined}
                    className={`flex shrink-0 cursor-pointer items-center gap-2 border border-[#0A0A0A] bg-[#0A0A0A] px-4 text-[11px] font-semibold uppercase tracking-[0.15em] text-white transition-colors hover:bg-[#D60024] hover:border-[#D60024] ${uploading ? "opacity-50 pointer-events-none" : ""}`}
                >
                    {uploading ? "Sending…" : "Upload +"}
                    <input type="file" accept="image/*" className="hidden" onChange={pick} disabled={uploading} />
                </label>
            </div>
            {value && (
                <img src={value} alt="" className="mt-2 h-20 w-auto border border-neutral-200 object-cover" onError={(e) => (e.currentTarget.style.display = "none")} />
            )}
        </Field>
    );
}
